import type { ReactNode } from "react"
import { useEffect } from "react"
import { clearAuth, getTokenAvailable, suspendAuth } from "./slice"
import { getRefreshToken } from "./refreshTokenStorage"
import { withRefreshLock } from "./refreshLock"
import { isRefreshTokenRejected } from "./refreshFailure"
import { useTokenRefreshMutation } from "@/base/baseApi"
import { useDispatch, useSelector } from "@/base/store"

type Props = {
  children: ReactNode
}

export default function AuthWrapper({ children }: Props) {
  const isTokenAvailable = useSelector(getTokenAvailable)
  const [tokenRefresh] = useTokenRefreshMutation()
  const dispatch = useDispatch()

  // Fetch auth token when missing from store
  useEffect(() => {
    if (isTokenAvailable) {
      // Already have an access token
      return
    }

    // Under the same lock as baseApi's 401 refresh: this one runs outside its mutex, and with a
    // rotating token two exchanges of the same value end the session. The token is read inside the
    // lock and nowhere else — a tab that waited here must use whatever the winner wrote, which is
    // also why there is no early check for one: the answer before waiting is not the answer after.
    void withRefreshLock(async () => {
      const refreshToken = getRefreshToken()
      if (!refreshToken) {
        dispatch(clearAuth())

        return
      }

      try {
        await tokenRefresh(refreshToken).unwrap()

        return
      } catch (error) {
        // Invalid refresh token — but only when the server actually said so. Anything else has
        // already been retried by the request itself, and leaves the token in place for the next
        // load to try again with.
        dispatch(
          isRefreshTokenRejected(error)
            ? clearAuth(refreshToken)
            : suspendAuth(),
        )
      }
    })
  }, [isTokenAvailable, dispatch, tokenRefresh])

  return children
}
